/**
 * Factores de pureza para diferentes quilatajes de oro y platino
 * Estos valores se usan para calcular el oro/platino fino a partir del peso total
 */
export const METAL_PURITY_FACTORS = {
  // Factores de pureza para oro por quilataje
  GOLD: {
    '9kt': 0.3792,
    '14kt': 0.5875,
    '18kt': 0.752,
    '19kt': 0.8042
  },
  // Factor de pureza para platino
  PLATINUM: 0.955,
  // Factor de pureza para plata (925 = 92.5% pureza)
  SILVER: 0.925
};

/**
 * Calcula el oro fino total a partir de los datos de necesidad por quilataje
 * @param {Object} needsData - Datos de necesidad por quilataje {9kt: valor, 14kt: valor, etc.}
 * @returns {number} Total de oro fino en gramos
 */
export const calculateFineGold = (needsData) => {
  if (!needsData || typeof needsData !== 'object') {
    return 0;
  }

  let totalFineGold = 0;
  
  // Procesar cada quilataje de oro
  Object.keys(METAL_PURITY_FACTORS.GOLD).forEach(kt => {
    const weight = parseFloat(needsData[kt]) || 0;
    const purityFactor = METAL_PURITY_FACTORS.GOLD[kt];
    totalFineGold += weight * purityFactor;
  });

  return totalFineGold;
};

/**
 * Calcula el platino fino total a partir de los datos de necesidad
 * @param {Object} needsData - Datos de necesidad {Platino: valor}
 * @returns {number} Total de platino fino en gramos
 */
export const calculateFinePlatinum = (needsData) => {
  if (!needsData || typeof needsData !== 'object') {
    return 0;
  }

  const platinumWeight = parseFloat(needsData.Platino) || 0;
  return platinumWeight * METAL_PURITY_FACTORS.PLATINUM;
};

/**
 * Calcula tanto oro fino como platino fino para una ubicación
 * @param {Object} needsData - Datos de necesidad de la ubicación
 * @returns {Object} {fineGold: number, finePlatinum: number}
 */
export const calculateFineMetals = (needsData) => {
  return {
    fineGold: calculateFineGold(needsData),
    finePlatinum: calculateFinePlatinum(needsData)
  };
};

/**
 * Obtiene el factor de pureza basado en la descripción de la aleación
 * @param {string} aleacion - Descripción de la aleación (ej: "Oro amarillo 14KT", "Platino con cobalto 955", "Plata")
 * @returns {number} Factor de pureza (0-1)
 */
export const getPurityFactor = (aleacion) => {
  if (!aleacion || typeof aleacion !== 'string') {
    return 1; // Si no se puede determinar, asumir 100% pureza
  }

  const aleacionLower = aleacion.toLowerCase();

  // Detectar oro y su quilataje
  if (aleacionLower.includes('oro')) {
    if (aleacionLower.includes('9kt') || aleacionLower.includes('9 kt')) {
      return METAL_PURITY_FACTORS.GOLD['9kt'];
    }
    if (aleacionLower.includes('14kt') || aleacionLower.includes('14 kt')) {
      return METAL_PURITY_FACTORS.GOLD['14kt'];
    }
    if (aleacionLower.includes('18kt') || aleacionLower.includes('18 kt')) {
      return METAL_PURITY_FACTORS.GOLD['18kt'];
    }
    if (aleacionLower.includes('19kt') || aleacionLower.includes('19 kt')) {
      return METAL_PURITY_FACTORS.GOLD['19kt'];
    }
    // Si es oro pero no se especifica quilataje, usar 14kt como default
    return METAL_PURITY_FACTORS.GOLD['14kt'];
  }

  // Detectar platino
  if (aleacionLower.includes('platino')) {
    return METAL_PURITY_FACTORS.PLATINUM;
  }

  // Detectar plata
  if (aleacionLower.includes('plata')) {
    return METAL_PURITY_FACTORS.SILVER;
  }

  // Si no se puede determinar el tipo de metal, asumir 100% pureza
  return 1;
};

/**
 * Convierte pesos brutos a pesos finos para datos de fundición
 * @param {Array} fundicionData - Array de datos de fundición con propiedades de aleación y pesos
 * @returns {Array} Array con los mismos datos pero con pesos convertidos a fino
 */
export const convertFundicionToFine = (fundicionData) => {
  if (!Array.isArray(fundicionData)) {
    return [];
  }

  return fundicionData.map(item => {
    const purityFactor = getPurityFactor(item.aleacion);
    
    // Crear una copia del item con los pesos convertidos a fino
    const fineItem = { ...item };
    
    // Convertir todos los campos numéricos (excepto aleacion y descripcion)
    Object.keys(fineItem).forEach(key => {
      if (key !== 'aleacion' && key !== 'descripcion' && typeof fineItem[key] === 'number') {
        fineItem[key] = fineItem[key] * purityFactor;
      }
    });

    return fineItem;
  });
};
